/**
 * Conciliação de ENTRADAS (0051; plano rev1 e mockups E3 aprovados em 25/10/2026). A conciliação só vincula.
 *  - E3-02/E3-04: entradas × recebimentos (RBM) ou recuperações (RCP) aguardando extrato; saídas × devoluções de crédito
 *    do pagador. Sugestões só sinalizadas — NADA vem marcado; soma exata (R$ 0,00).
 *  - E3-03: registrar recebimento e conciliar — distribuir a entrada entre notas e avulsos; prévia; uma operação só.
 *  - E3-05: identificar a entrada (escolha explícita; não é receita; o movimento continua no caixa com a natureza).
 *  - E3-09: devolução de fornecedor — recuperar o crédito a recuperar do Pagar (até o saldo) e conciliar; sem crédito,
 *    "pendente de vínculo"; depois, vincular ao crédito.
 */
import { useMemo, useState, type ReactNode } from 'react';
import { api } from '../api.ts';
import { Ajuda, EstadoCarga, Modal, RolagemIndicada, SeloSituacao, useCarga } from '../comercial/ui.tsx';
import { dataBr, novaChave, rs } from './ContasPagar.tsx';
import { Campo } from '../componentes/basicos.tsx';

const msg = (e: unknown) => (e as Error).message;
const centavos = (v: string | number) => Math.round(Number(String(v).includes(',') ? String(v).replace(/\./g, '').replace(',', '.') : v) * 100);
const valorColorido = (v: string) => (
  <span className={Number(v) < 0 ? 'conc-saida' : 'conc-entrada'}>
    {Number(v) > 0 ? '+' : ''}
    {rs(v)}
  </span>
);
const totais = (rotulos: [string, number][], dif: number, marcado: boolean, teste: string) => (
  <p className="faixa-resumo-modal" data-teste={teste}>
    {rotulos.map(([r, v]) => (
      <span key={r}>
        {r} <strong>{rs(v / 100)}</strong>
      </span>
    ))}
    <span className={dif === 0 && marcado ? 'conc-ok' : 'conc-dif'} data-teste={`${teste}-diferenca`}>
      Diferença {rs(Math.abs(dif) / 100)} {dif === 0 && marcado ? '✓' : ''}
    </span>
  </p>
);
const Erro = ({ texto, teste }: { texto: string; teste: string }) =>
  texto ? (
    <p className="aviso aviso-erro" role="alert" data-teste={teste}>
      {texto}
    </p>
  ) : null;
function Movs({ movimentos }: { movimentos: { numero: string; data: string; valor: string; descricao: string; identificador?: string | null }[] }) {
  return (
    <div className="faixa-resumo-modal conc-bloco">
      {movimentos.map((m) => (
        <div key={m.numero}>
          <strong>{m.numero}</strong> · {dataBr(m.data)} · {m.descricao} · {valorColorido(m.valor)}
          {m.identificador ? <span className="texto-secundario"> · identificador {m.identificador}</span> : null}
        </div>
      ))}
    </div>
  );
}
const Sugerido = ({ motivo }: { motivo: string }) => (
  <div>
    <SeloSituacao texto={`Sugerido · ${motivo}`} tom="positivo" />
  </div>
);
/** Vários itens casam com a mesma entrada: nenhum é sugerido; a escolha é explícita (nada vem marcado). */
const Ambiguo = ({ n }: { n: number }) => (
  <div data-teste="candidato-ambiguo">
    <SeloSituacao texto={`Candidato · ambíguo (${n})`} tom="parcial" />
  </div>
);

/* ---------------------------------------------------------------- E3-02 · E3-04: conciliar com o que já existe */
interface CandidatosEntrada {
  janelaDias: number;
  natureza: 'entrada' | 'saida';
  movimentos: { numero: string; data: string; valor: string; descricao: string; identificador: string | null }[];
  total: string;
  recebimentos: { numero: string; data: string; valor: string; documento: string; tipoDocumento: 'nota' | 'avulso'; pagador: string | null; registro: string | null; retencoes: { ir: string | null; antecipacao: string | null }; sugerido: boolean; ambiguo: number | null }[];
  recuperacoes: { numero: string; data: string; valor: string; obrigacao: string; descricao: string; favorecido: string | null; sugerido: boolean; ambiguo: number | null }[];
  devolucoes: { id: string; credito: string; data: string; valor: string; identificador: string | null; pagador: string | null; sugerido: boolean; ambiguo: number | null }[];
  permissoes: { registrarRecebimento: boolean; registrarRecuperacao: boolean };
}
export function ConciliarEntradas({ movimentos, aoFechar, aoConcluir, aoRegistrar, aoIdentificar }: { movimentos: string[]; aoFechar: () => void; aoConcluir: () => void; aoRegistrar: (mov: string) => void; aoIdentificar: (mov: string) => void }) {
  const [busca, setBusca] = useState('');
  const [aplicada, setAplicada] = useState('');
  const carga = useCarga(() => api.get<CandidatosEntrada>(`/api/conciliacao/entradas/candidatos?movimentos=${encodeURIComponent(movimentos.join(','))}${aplicada ? `&busca=${encodeURIComponent(aplicada)}` : ''}`), [movimentos.join(','), aplicada]);
  const [rbm, setRbm] = useState<Record<string, number>>({});
  const [rcp, setRcp] = useState<Record<string, number>>({});
  const [dev, setDev] = useState<Record<string, number>>({});
  const [erro, setErro] = useState('');
  const [enviando, setEnviando] = useState(false);
  const c = carga.dados;
  const total = c ? centavos(c.total) : 0;
  const soma = [rbm, rcp, dev].reduce((s, m) => s + Object.values(m).reduce((a, b) => a + b, 0), 0);
  const marcado = soma > 0;
  const misturou = Object.keys(rcp).length > 0 && Object.keys(rbm).length + Object.keys(dev).length > 0;
  const dif = total - soma;
  const alternar = (set: typeof setRbm, chave: string, valor: number) => set((m) => (m[chave] !== undefined ? Object.fromEntries(Object.entries(m).filter(([k]) => k !== chave)) : { ...m, [chave]: valor }));
  const enviar = async () => {
    setEnviando(true);
    setErro('');
    try {
      const todosSugeridos = c!.recebimentos.filter((r) => rbm[r.numero] !== undefined).every((r) => r.sugerido) && c!.recuperacoes.filter((r) => rcp[r.numero] !== undefined).every((r) => r.sugerido);
      await api.post('/api/conciliacao/entradas/conciliacoes', { movimentos, recebimentos: Object.keys(rbm), recuperacoes: Object.keys(rcp), devolucoes: Object.keys(dev), origem: todosSugeridos ? 'sugestao' : 'manual' });
      aoConcluir();
    } catch (e) {
      setErro(msg(e));
    } finally {
      setEnviando(false);
    }
  };
  const entrada = c?.natureza === 'entrada';
  return (
    <Modal
      titulo={entrada ? 'Conciliar entrada com o que já foi registrado' : 'Conciliar saída com devolução de crédito do pagador'}
      aoFechar={aoFechar}
      largo
      rodape={
        <>
          <span className="texto-secundario conc-rodape-efeito">Nada é gravado até confirmar.</span>
          <button type="button" className="botao-secundario" onClick={aoFechar}>
            Voltar
          </button>
          <button type="button" className="botao-primario" disabled={!c || !marcado || dif !== 0 || misturou || enviando} onClick={() => void enviar()} data-teste="confirmar-conciliar-entrada">
            Conciliar
          </button>
        </>
      }
    >
      {!c ? (
        <EstadoCarga carregando={carga.carregando} erro={carga.erro} recarregar={carga.recarregar} teste="candidatos-entrada" />
      ) : (
        <div data-teste="modal-conciliar-entrada">
          <Movs movimentos={c.movimentos} />
          {entrada ? (
            <>
              <h3 className="conc-titulo">
                Recebimentos aguardando extrato (Contas a Receber)
                <Ajuda titulo="Recebimentos">{`Recebimentos (RBM) desta conta ainda não conciliados. "Sugerido": mesmo valor em dinheiro e data até ${c.janelaDias} dias — a sugestão vem DESMARCADA; marque e confirme. IR e antecipação não passam pelo banco: ficam no recebimento e não entram na soma.`}</Ajuda>
              </h3>
              <div className="conc-busca">
                <input aria-label="Buscar recebimento" placeholder="RBM, nota, avulso ou pagador" value={busca} onChange={(e) => setBusca(e.target.value)} data-teste="busca-entrada" />
                <button type="button" className="botao-secundario pequeno" onClick={() => setAplicada(busca)}>
                  Buscar
                </button>
              </div>
              {c.recebimentos.length ? (
                <RolagemIndicada className="rolagem quadro-tabela">
                  <table className="tabela tabela-compacta" data-teste="recebimentos-conciliar">
                    <thead>
                      <tr>
                        <th className="col-selecao" aria-label="Marcar" />
                        <th>Recebimento</th>
                        <th>Data efetiva</th>
                        <th>Origem</th>
                        <th className="numero">Em dinheiro</th>
                        <th className="numero">Retenções (fora do banco)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {c.recebimentos.map((r) => (
                        <tr key={r.numero} data-teste={`rbm-${r.numero}`}>
                          <td className="col-selecao">
                            <input type="checkbox" aria-label={`Marcar ${r.numero}`} checked={rbm[r.numero] !== undefined} onChange={() => alternar(setRbm, r.numero, centavos(r.valor))} data-teste={`marcar-rbm-${r.numero}`} />
                          </td>
                          <td className="conc-nowrap">
                            <strong>{r.numero}</strong>
                            {r.sugerido && <Sugerido motivo="mesmo valor e conta" />}
                            {r.ambiguo && <Ambiguo n={r.ambiguo} />}
                          </td>
                          <td className="conc-nowrap">{dataBr(r.data)}</td>
                          <td>
                            {r.documento} · {r.tipoDocumento === 'nota' ? 'nota da Inttrix' : 'avulso'}
                            <div className="texto-secundario conc-mini">{[r.pagador, r.registro ? `registro ${r.registro}` : null].filter(Boolean).join(' · ')}</div>
                          </td>
                          <td className="numero conc-nowrap">{rs(r.valor)}</td>
                          <td className="numero">{[r.retencoes.ir ? `IR ${rs(r.retencoes.ir)}` : null, r.retencoes.antecipacao ? `antecip. ${rs(r.retencoes.antecipacao)}` : null].filter(Boolean).join(' · ') || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </RolagemIndicada>
              ) : (
                <p className="texto-secundario">Nenhum recebimento aguardando extrato nesta conta{aplicada ? ' para esta busca' : ''}.</p>
              )}
              {c.recuperacoes.length > 0 && (
                <>
                  <h3 className="conc-titulo">
                    Recuperações aguardando extrato (Contas a Pagar)
                    <Ajuda titulo="Recuperações">Devoluções em dinheiro do crédito a recuperar já registradas na ficha da despesa. Concilie em operação separada dos recebimentos.</Ajuda>
                  </h3>
                  <RolagemIndicada className="rolagem quadro-tabela">
                    <table className="tabela tabela-compacta" data-teste="recuperacoes-conciliar">
                      <thead>
                        <tr>
                          <th className="col-selecao" aria-label="Marcar" />
                          <th>Recuperação</th>
                          <th>Data</th>
                          <th>Obrigação · favorecido</th>
                          <th className="numero">Valor</th>
                        </tr>
                      </thead>
                      <tbody>
                        {c.recuperacoes.map((r) => (
                          <tr key={r.numero} data-teste={`rcp-${r.numero}`}>
                            <td className="col-selecao">
                              <input type="checkbox" aria-label={`Marcar ${r.numero}`} checked={rcp[r.numero] !== undefined} onChange={() => alternar(setRcp, r.numero, centavos(r.valor))} data-teste={`marcar-rcp-${r.numero}`} />
                            </td>
                            <td className="conc-nowrap">
                              <strong>{r.numero}</strong>
                              {r.sugerido && <Sugerido motivo="mesmo valor" />}
                              {r.ambiguo && <Ambiguo n={r.ambiguo} />}
                            </td>
                            <td className="conc-nowrap">{dataBr(r.data)}</td>
                            <td>
                              {r.obrigacao} · {r.favorecido ?? '—'}
                              <div className="texto-secundario conc-mini">{r.descricao}</div>
                            </td>
                            <td className="numero conc-nowrap">{rs(r.valor)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </RolagemIndicada>
                </>
              )}
            </>
          ) : c.devolucoes.length ? (
            <RolagemIndicada className="rolagem quadro-tabela">
              <table className="tabela tabela-compacta" data-teste="devolucoes-conciliar">
                <thead>
                  <tr>
                    <th className="col-selecao" aria-label="Marcar" />
                    <th>Devolução de crédito</th>
                    <th>Data</th>
                    <th>Pagador</th>
                    <th className="numero">Valor</th>
                  </tr>
                </thead>
                <tbody>
                  {c.devolucoes.map((d) => (
                    <tr key={d.id} data-teste={`dev-${d.credito}`}>
                      <td className="col-selecao">
                        <input type="checkbox" aria-label={`Marcar devolução do ${d.credito}`} checked={dev[d.id] !== undefined} onChange={() => alternar(setDev, d.id, centavos(d.valor))} data-teste={`marcar-dev-${d.credito}`} />
                      </td>
                      <td className="conc-nowrap">
                        <strong>{d.credito}</strong>
                        {d.sugerido && <Sugerido motivo="mesmo valor" />}
                        {d.ambiguo && <Ambiguo n={d.ambiguo} />}
                      </td>
                      <td className="conc-nowrap">{dataBr(d.data)}</td>
                      <td>{d.pagador ?? '—'}</td>
                      <td className="numero conc-nowrap">{rs(d.valor)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </RolagemIndicada>
          ) : (
            <p className="texto-secundario">Nenhuma devolução de crédito do pagador aguardando extrato nesta conta.</p>
          )}
          {totais([[entrada ? 'Entradas' : 'Saídas', total], ['Itens marcados', soma]], dif, marcado, 'totais-entrada')}
          {misturou && <p className="aviso aviso-atencao-fin">Recebimentos (Receber) e recuperações (Pagar) se conciliam em operações separadas.</p>}
          {marcado && dif !== 0 && <p className="aviso aviso-erro">As somas não fecham: a conciliação só é confirmada com diferença zero.</p>}
          {entrada && movimentos.length === 1 && (
            <p className="texto-secundario" data-teste="alternativas-entrada">
              Não há registro?{' '}
              {c.permissoes.registrarRecebimento && (
                <button type="button" className="botao-link" onClick={() => aoRegistrar(movimentos[0])} data-teste="abrir-registrar-recebimento">
                  Registrar recebimento e conciliar
                </button>
              )}
              {c.permissoes.registrarRecebimento && ' · '}
              <button type="button" className="botao-link" onClick={() => aoIdentificar(movimentos[0])} data-teste="abrir-identificar">
                Identificar (devolução de fornecedor, aporte, empréstimo…)
              </button>
            </p>
          )}
          <Erro texto={erro} teste="erro-conciliar-entrada" />
        </div>
      )}
    </Modal>
  );
}

/* ---------------------------------------------------------------- E3-03: registrar recebimento e conciliar */
interface DocAberto { tipo: 'nota' | 'avulso'; numero: string; numeroFiscal: string | null; descricao?: string; pagador: string | null; valor: string; saldo: string; ir: { percentual: string; base: string; valor: string } | null; sugerido: boolean; ambiguo: number | null }
interface DocsAbertos { movimento: { numero: string; data: string; valor: string; descricao: string; identificador: string | null }; documentos: DocAberto[]; podeRegistrar: boolean }
interface Linha { valor: string; ir: string; antecipacao: string }
export function RegistrarRecebimentoConciliar({ movimento, aoFechar, aoConcluir }: { movimento: string; aoFechar: () => void; aoConcluir: () => void }) {
  const [busca, setBusca] = useState('');
  const [aplicada, setAplicada] = useState('');
  const carga = useCarga(() => api.get<DocsAbertos>(`/api/conciliacao/entradas/documentos-abertos?movimento=${movimento}${aplicada ? `&busca=${encodeURIComponent(aplicada)}` : ''}`), [movimento, aplicada]);
  const [marcados, setMarcados] = useState<Record<string, Linha>>({});
  const [excedente, setExcedente] = useState(false);
  const [previa, setPrevia] = useState<{ efeitos: { alvo: string; texto: string }[] } | null>(null);
  const [chave] = useState(novaChave);
  const [erro, setErro] = useState('');
  const [enviando, setEnviando] = useState(false);
  const d = carga.dados;
  const entrada = d ? centavos(d.movimento.valor) : 0;
  const soma = Object.values(marcados).reduce((s, l) => s + (centavos(l.valor || '0') || 0), 0);
  const dif = entrada - soma;
  const docs = useMemo(() => d?.documentos ?? [], [d]);
  const corpo = () => ({
    movimento,
    chave_idempotencia: chave,
    documentos: Object.entries(marcados).map(([numero, l]) => {
      const doc = docs.find((x) => x.numero === numero)!;
      return {
        [doc.tipo]: numero,
        valor: l.valor,
        ir: l.ir ? { percentual: l.ir } : undefined,
        antecipacao: l.antecipacao ? { tipo: 'valor', valor: l.antecipacao } : undefined,
        excedente: excedente ? 'credito' : undefined,
      };
    }),
  });
  const alternar = (doc: DocAberto) => {
    setPrevia(null);
    setMarcados((m) => {
      if (m[doc.numero]) return Object.fromEntries(Object.entries(m).filter(([k]) => k !== doc.numero));
      const falta = Math.max(entrada - Object.values(m).reduce((s, l) => s + (centavos(l.valor || '0') || 0), 0), 0);
      const liquido = centavos(doc.saldo) - (doc.ir ? centavos(doc.ir.valor) : 0);
      return { ...m, [doc.numero]: { valor: (Math.min(falta, liquido) / 100).toFixed(2).replace('.', ','), ir: doc.ir ? String(doc.ir.percentual).replace('.', ',') : '', antecipacao: '' } };
    });
  };
  const mudar = (numero: string, campo: keyof Linha, v: string) => (setPrevia(null), setMarcados((m) => ({ ...m, [numero]: { ...m[numero], [campo]: v } })));
  const chamar = async (comPrevia: boolean) => {
    setEnviando(true);
    setErro('');
    try {
      const r = await api.post<{ efeitos: { alvo: string; texto: string }[] }>('/api/conciliacao/entradas/registrar-recebimento', { ...corpo(), previa: comPrevia });
      if (comPrevia) setPrevia(r);
      else aoConcluir();
    } catch (e) {
      setErro(msg(e));
    } finally {
      setEnviando(false);
    }
  };
  const pronto = Object.keys(marcados).length > 0 && dif === 0;
  return (
    <Modal
      titulo="Registrar recebimento e conciliar"
      aoFechar={aoFechar}
      largo
      rodape={
        <>
          <span className="texto-secundario conc-rodape-efeito">Exige: conciliar + registrar recebimento.</span>
          <button type="button" className="botao-secundario" onClick={aoFechar}>
            Voltar
          </button>
          <button type="button" className="botao-secundario" disabled={!pronto || enviando} onClick={() => void chamar(true)} data-teste="ver-previa-registrar">
            Ver prévia
          </button>
          <button type="button" className="botao-primario" disabled={!pronto || !previa || enviando} onClick={() => void chamar(false)} data-teste="confirmar-registrar-recebimento">
            Registrar e conciliar
          </button>
        </>
      }
    >
      {!d ? (
        <EstadoCarga carregando={carga.carregando} erro={carga.erro} recarregar={carga.recarregar} teste="documentos-abertos" />
      ) : (
        <div data-teste="modal-registrar-recebimento">
          <Movs movimentos={[d.movimento]} />
          <p className="texto-secundario conc-mini">
            Data, conta e identificador vêm da entrada e não se editam. Um registro da entrada (REN) guarda o identificador bancário — único na conta —; cada documento ganha o seu recebimento (RBM), e tudo é gravado numa operação só.
            <Ajuda titulo="Uma entrada, vários documentos">Repetir a operação não duplica (mesma chave). Se outra pessoa registrar a mesma entrada ao mesmo tempo, a segunda recebe “entrada já registrada”. Se qualquer documento falhar (por exemplo, quitado um instante antes), nada é gravado.</Ajuda>
          </p>
          <div className="conc-busca">
            <input aria-label="Buscar documento" placeholder="Pagador, NFI, nº fiscal, RAV ou descrição" value={busca} onChange={(e) => setBusca(e.target.value)} data-teste="busca-documento" />
            <button type="button" className="botao-secundario pequeno" onClick={() => setAplicada(busca)}>
              Buscar
            </button>
          </div>
          {docs.length ? (
            <RolagemIndicada className="rolagem quadro-tabela">
              <table className="tabela tabela-compacta" data-teste="documentos-registrar">
                <thead>
                  <tr>
                    <th className="col-selecao" aria-label="Marcar" />
                    <th>Documento · pagador</th>
                    <th className="numero">Saldo</th>
                    <th>IR % · antecipação R$</th>
                    <th className="numero">Em dinheiro nesta entrada</th>
                  </tr>
                </thead>
                <tbody>
                  {docs.map((doc) => {
                    const l = marcados[doc.numero];
                    return (
                      <tr key={doc.numero} data-teste={`doc-${doc.numero}`}>
                        <td className="col-selecao">
                          <input type="checkbox" aria-label={`Marcar ${doc.numero}`} checked={Boolean(l)} onChange={() => alternar(doc)} data-teste={`marcar-doc-${doc.numero}`} />
                        </td>
                        <td>
                          <strong>{doc.numero}</strong> · {doc.tipo === 'nota' ? `nota da Inttrix${doc.numeroFiscal ? ` ${doc.numeroFiscal}` : ''}` : `avulso · ${doc.descricao ?? ''}`}
                          {doc.sugerido && <Sugerido motivo="mesmo valor" />}
                          {doc.ambiguo && <Ambiguo n={doc.ambiguo} />}
                          <div className="texto-secundario conc-mini">{doc.pagador ?? '—'}</div>
                        </td>
                        <td className="numero conc-nowrap">{rs(doc.saldo)}</td>
                        <td>
                          {l ? (
                            <span className="conc-linha-campos">
                              {doc.tipo === 'nota' && <input aria-label={`IR % de ${doc.numero}`} className="conc-campo-curto" placeholder="IR %" value={l.ir} onChange={(e) => mudar(doc.numero, 'ir', e.target.value)} data-teste={`ir-doc-${doc.numero}`} />}
                              <input aria-label={`Antecipação de ${doc.numero}`} className="conc-campo-curto" placeholder="Antecip. R$" value={l.antecipacao} onChange={(e) => mudar(doc.numero, 'antecipacao', e.target.value)} data-teste={`antecipacao-doc-${doc.numero}`} />
                            </span>
                          ) : (
                            <span className="texto-secundario">{doc.ir ? `IR destacado ${doc.ir.percentual}%` : '—'}</span>
                          )}
                        </td>
                        <td className="numero">{l ? <input aria-label={`Valor em dinheiro de ${doc.numero}`} className="conc-campo-valor" value={l.valor} onChange={(e) => mudar(doc.numero, 'valor', e.target.value)} data-teste={`valor-doc-${doc.numero}`} /> : '—'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </RolagemIndicada>
          ) : (
            <p className="texto-secundario">Nenhuma nota ou avulso em aberto{aplicada ? ' para esta busca' : ''}.</p>
          )}
          {totais([['Entrada no banco', entrada], ['Distribuído em dinheiro', soma]], dif, Object.keys(marcados).length > 0, 'totais-registrar')}
          <label className="conc-check">
            <input type="checkbox" checked={excedente} onChange={(e) => (setPrevia(null), setExcedente(e.target.checked))} data-teste="excedente-credito" /> Se passar do saldo, registrar o excedente como crédito do pagador (escolha explícita)
          </label>
          {previa && (
            <div className="aviso aviso-sucesso" data-teste="previa-registrar">
              <strong>Prévia (nada gravado):</strong> registro da entrada (REN) + {previa.efeitos.length} recebimento(s) + conciliação.
              <ul className="conc-efeitos">
                {previa.efeitos.map((e) => (
                  <li key={e.alvo}>
                    {e.alvo}: {e.texto}
                  </li>
                ))}
              </ul>
            </div>
          )}
          <Erro texto={erro} teste="erro-registrar-recebimento" />
        </div>
      )}
    </Modal>
  );
}

/* ---------------------------------------------------------------- E3-05: identificar entrada */
const NATUREZAS: [string, string, ReactNode][] = [
  ['recebimento', 'Recebimento', 'nota da Inttrix, comissão ou avulso → "Registrar recebimento e conciliar"'],
  ['devolucao_fornecedor', 'Devolução de fornecedor', 'vincular ao crédito a recuperar do Contas a Pagar; sem crédito, fica "pendente de vínculo"'],
  ['aporte', 'Aporte', 'sócio ou empresa do grupo'],
  ['emprestimo', 'Empréstimo', 'recebido'],
  ['rendimento', 'Rendimento da conta', 'remuneração da conta corrente (resgates de RDB se conciliam com o evento da aplicação)'],
  ['estorno_banco', 'Estorno do banco', 'referência opcional à saída estornada'],
  ['transferencia', 'Transferência entre contas da Inttrix', 'usa o par da outra conta (fluxo atual)'],
  ['outra', 'Outra', 'descrição obrigatória'],
];
export function IdentificarEntrada({ movimento, aoFechar, aoConcluir, aoRecebimento, aoDevolucao, aoTransferencia }: { movimento: string; aoFechar: () => void; aoConcluir: () => void; aoRecebimento: () => void; aoDevolucao: () => void; aoTransferencia: () => void }) {
  const carga = useCarga(() => api.get<{ numero: string; data: string; valor: string; descricao: string; identificador: string | null }>(`/api/conciliacao/movimentos/${movimento}`), [movimento]);
  const [natureza, setNatureza] = useState('');
  const [descricao, setDescricao] = useState('');
  const [observacao, setObservacao] = useState('');
  const [movRef, setMovRef] = useState('');
  const [erro, setErro] = useState('');
  const [enviando, setEnviando] = useState(false);
  const desvia = natureza === 'recebimento' || natureza === 'devolucao_fornecedor' || natureza === 'transferencia';
  const continuar = async () => {
    if (natureza === 'recebimento') return aoRecebimento();
    if (natureza === 'devolucao_fornecedor') return aoDevolucao();
    if (natureza === 'transferencia') return aoTransferencia();
    setEnviando(true);
    setErro('');
    try {
      await api.post('/api/conciliacao/entradas/identificar', { movimento, natureza, descricao, observacao: observacao || undefined, movimento_ref: movRef || undefined });
      aoConcluir();
    } catch (e) {
      setErro(msg(e));
    } finally {
      setEnviando(false);
    }
  };
  return (
    <Modal
      titulo="Identificar entrada"
      aoFechar={aoFechar}
      largo
      rodape={
        <>
          <span className="texto-secundario conc-rodape-efeito">{desvia ? 'Continua no fluxo próprio.' : 'Não é receita: o movimento continua no caixa com a natureza.'}</span>
          <button type="button" className="botao-secundario" onClick={aoFechar}>
            Voltar
          </button>
          <button type="button" className="botao-primario" disabled={!natureza || (!desvia && descricao.trim().length < 3) || enviando} onClick={() => void continuar()} data-teste="confirmar-identificar">
            {desvia ? 'Continuar' : 'Identificar'}
          </button>
        </>
      }
    >
      {!carga.dados ? (
        <EstadoCarga carregando={carga.carregando} erro={carga.erro} recarregar={carga.recarregar} teste="identificar" />
      ) : (
        <div data-teste="modal-identificar">
          <Movs movimentos={[carga.dados]} />
          <p className="texto-secundario conc-mini">
            Escolha explícita da natureza — nada é classificado sozinho e nada vira receita automaticamente. O movimento continua no saldo e alimentará o Fluxo de Caixa uma única vez, com a natureza escolhida.
          </p>
          <div role="radiogroup" aria-label="Natureza da entrada" className="conc-naturezas">
            {NATUREZAS.map(([k, rotulo, dica]) => (
              <label key={k} className="conc-natureza" data-teste={`natureza-${k}`}>
                <input type="radio" name="natureza" checked={natureza === k} onChange={() => setNatureza(k)} />
                <span>
                  <strong>{rotulo}</strong> · <span className="texto-secundario">{dica}</span>
                </span>
              </label>
            ))}
          </div>
          {!desvia && natureza && (
            <div className="grade-form dois">
              <Campo rotulo="Descrição *">{(id) => <input id={id} maxLength={300} value={descricao} onChange={(e) => setDescricao(e.target.value)} data-teste="descricao-identificacao" />}</Campo>
              <Campo rotulo="Observação (opcional)">{(id) => <input id={id} maxLength={500} value={observacao} onChange={(e) => setObservacao(e.target.value)} data-teste="observacao-identificacao" />}</Campo>
              {natureza === 'estorno_banco' && <Campo rotulo="Saída estornada (MOV-, opcional)">{(id) => <input id={id} maxLength={20} value={movRef} onChange={(e) => setMovRef(e.target.value)} />}</Campo>}
            </div>
          )}
          <Erro texto={erro} teste="erro-identificar" />
        </div>
      )}
    </Modal>
  );
}

/* ---------------------------------------------------------------- E3-09: devolução de fornecedor × crédito a recuperar */
interface CreditoCand {
  obrigacaoId: string;
  obrigacao: string;
  despesa: string;
  descricao: string;
  favorecido: string | null;
  gerado: string;
  recuperado: string;
  saldo: string;
  origens: { tipo: 'documento' | 'pagamento_a_maior'; valor: string; pagamento: string | null }[];
  sugerido: boolean;
  ambiguo: number | null;
}
export function RecuperarCredito({ movimento, identificacao, aoFechar, aoConcluir }: { movimento: string; identificacao?: string; aoFechar: () => void; aoConcluir: () => void }) {
  const [busca, setBusca] = useState('');
  const [aplicada, setAplicada] = useState('');
  const carga = useCarga(() => api.get<{ movimento: { numero: string; data: string; valor: string; descricao: string }; creditos: CreditoCand[]; podeRegistrar: boolean }>(`/api/conciliacao/entradas/creditos-recuperar?movimento=${movimento}${aplicada ? `&busca=${encodeURIComponent(aplicada)}` : ''}`), [movimento, aplicada]);
  const [marcados, setMarcados] = useState<Record<string, string>>({});
  const [descricao, setDescricao] = useState('');
  const [chave] = useState(novaChave);
  const [erro, setErro] = useState('');
  const [enviando, setEnviando] = useState(false);
  const d = carga.dados;
  const entrada = d ? centavos(d.movimento.valor) : 0;
  const soma = Object.values(marcados).reduce((s, v) => s + (centavos(v || '0') || 0), 0);
  const dif = entrada - soma;
  const acima = d ? d.creditos.some((x) => marcados[x.obrigacao] !== undefined && centavos(marcados[x.obrigacao] || '0') > centavos(x.saldo)) : false;
  const alternar = (x: CreditoCand) =>
    setMarcados((m) => {
      if (m[x.obrigacao] !== undefined) return Object.fromEntries(Object.entries(m).filter(([k]) => k !== x.obrigacao));
      const falta = Math.max(entrada - Object.values(m).reduce((s, v) => s + (centavos(v || '0') || 0), 0), 0);
      return { ...m, [x.obrigacao]: (Math.min(falta, centavos(x.saldo)) / 100).toFixed(2).replace('.', ',') };
    });
  const recuperar = async () => {
    setEnviando(true);
    setErro('');
    try {
      const recuperacoes = Object.entries(marcados).map(([obrigacao, valor]) => ({ obrigacao, valor }));
      if (identificacao) await api.post(`/api/conciliacao/identificacoes/${identificacao}/vincular-credito`, { recuperacoes, chave_idempotencia: chave });
      else await api.post('/api/conciliacao/entradas/recuperar', { movimento, recuperacoes, chave_idempotencia: chave });
      aoConcluir();
    } catch (e) {
      setErro(msg(e));
    } finally {
      setEnviando(false);
    }
  };
  const pendente = async () => {
    setEnviando(true);
    setErro('');
    try {
      await api.post('/api/conciliacao/entradas/identificar', { movimento, natureza: 'devolucao_fornecedor', descricao });
      aoConcluir();
    } catch (e) {
      setErro(msg(e));
    } finally {
      setEnviando(false);
    }
  };
  return (
    <Modal
      titulo={identificacao ? 'Vincular devolução ao crédito a recuperar' : 'Devolução de fornecedor · crédito a recuperar'}
      aoFechar={aoFechar}
      largo
      rodape={
        <>
          <button type="button" className="botao-secundario" onClick={aoFechar}>
            Voltar
          </button>
          {!identificacao && (
            <button type="button" className="botao-secundario" disabled={descricao.trim().length < 3 || enviando} onClick={() => void pendente()} data-teste="pendente-vinculo">
              Registrar como pendente de vínculo
            </button>
          )}
          <button type="button" className="botao-primario" disabled={!d?.podeRegistrar || !Object.keys(marcados).length || dif !== 0 || acima || enviando} onClick={() => void recuperar()} data-teste="confirmar-recuperar">
            {identificacao ? 'Vincular e conciliar' : 'Recuperar e conciliar'}
          </button>
        </>
      }
    >
      {!d ? (
        <EstadoCarga carregando={carga.carregando} erro={carga.erro} recarregar={carga.recarregar} teste="creditos-recuperar" />
      ) : (
        <div data-teste="modal-recuperar">
          <Movs movimentos={[d.movimento]} />
          <p className="texto-secundario conc-mini">
            A entrada encerra, total ou parcialmente, o crédito a recuperar do Contas a Pagar — crédito do documento confirmado abaixo do pago ou pagamento indevido a maior. O pagamento original não muda; nenhum recebível novo é criado; nunca acima do saldo (fatura e dinheiro usam o mesmo saldo).
          </p>
          <div className="conc-busca">
            <input aria-label="Buscar crédito" placeholder="Favorecido, PAG- ou descrição" value={busca} onChange={(e) => setBusca(e.target.value)} data-teste="busca-credito" />
            <button type="button" className="botao-secundario pequeno" onClick={() => setAplicada(busca)}>
              Buscar
            </button>
          </div>
          {d.creditos.length ? (
            <RolagemIndicada className="rolagem quadro-tabela">
              <table className="tabela tabela-compacta" data-teste="creditos-lista">
                <thead>
                  <tr>
                    <th className="col-selecao" aria-label="Marcar" />
                    <th>Obrigação · favorecido</th>
                    <th>Origem do crédito</th>
                    <th className="numero">Gerado</th>
                    <th className="numero">Já recuperado</th>
                    <th className="numero">Saldo</th>
                    <th className="numero">Recuperar</th>
                  </tr>
                </thead>
                <tbody>
                  {d.creditos.map((x) => (
                    <tr key={x.obrigacao} data-teste={`credito-${x.obrigacao}`}>
                      <td className="col-selecao">
                        <input type="checkbox" aria-label={`Marcar ${x.obrigacao}`} checked={marcados[x.obrigacao] !== undefined} onChange={() => alternar(x)} data-teste={`marcar-credito-${x.obrigacao}`} />
                      </td>
                      <td>
                        <strong>{x.obrigacao}</strong> · {x.favorecido ?? '—'}
                        {x.sugerido && <Sugerido motivo="mesmo valor" />}
                        {x.ambiguo && <Ambiguo n={x.ambiguo} />}
                        <div className="texto-secundario conc-mini">{x.descricao}</div>
                      </td>
                      <td className="conc-mini">{x.origens.map((o) => (o.tipo === 'documento' ? `documento ${rs(o.valor)}` : `pago a maior ${rs(o.valor)}${o.pagamento ? ` (${o.pagamento})` : ''}`)).join(' · ')}</td>
                      <td className="numero conc-nowrap">{rs(x.gerado)}</td>
                      <td className="numero conc-nowrap">{rs(x.recuperado)}</td>
                      <td className="numero conc-nowrap">{rs(x.saldo)}</td>
                      <td className="numero">
                        {marcados[x.obrigacao] !== undefined ? (
                          <input aria-label={`Recuperar de ${x.obrigacao}`} className="conc-campo-valor" value={marcados[x.obrigacao]} onChange={(e) => setMarcados((m) => ({ ...m, [x.obrigacao]: e.target.value }))} data-teste={`valor-credito-${x.obrigacao}`} />
                        ) : (
                          '—'
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </RolagemIndicada>
          ) : (
            <p className="texto-secundario" data-teste="sem-creditos">
              Nenhum crédito a recuperar com saldo{aplicada ? ' para esta busca' : ''}.
            </p>
          )}
          {totais([['Entrada', entrada], ['Recuperado', soma]], dif, Object.keys(marcados).length > 0, 'totais-recuperar')}
          {acima && <p className="aviso aviso-erro">Um valor passa do saldo a recuperar do crédito: nunca acima do saldo.</p>}
          {!d.podeRegistrar && <p className="aviso aviso-atencao-fin">Seu perfil pode conciliar, mas não registrar a recuperação (Contas a Pagar: registrar pagamento).</p>}
          {!identificacao && (
            <Campo rotulo="Sem crédito identificado? Descrição para registrar como pendente de vínculo">
              {(id) => <input id={id} maxLength={300} value={descricao} onChange={(e) => setDescricao(e.target.value)} placeholder="Ex.: devolução do frete cobrado em duplicidade" data-teste="descricao-pendente" />}
            </Campo>
          )}
          <Erro texto={erro} teste="erro-recuperar" />
        </div>
      )}
    </Modal>
  );
}
